import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandXXL } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img/index.tsx';
import {
  NextSetStartCountdown,
  useNextSetStartTimer,
} from '@sorare/core/src/components/commonGame/NextSetStartCountdown/index.tsx';
import { AbsoluteNav } from '@sorare/core/src/components/navigation/AppNavigation/index.tsx';
import { ANY_SPORT_SERIES_ONBOARDING_SELECT_COMPETITION } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop.ts';
import { useGetCardSetConfig } from '@sorare/core/src/set';
import { useFindLatestCardSetConfig } from '@sorare/core/src/set/hooks.ts';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';

import { ClaimWelcomeBackRewards } from './ClaimWelcomeBackRewards/index.tsx';
import type { ClaimStarterPack_currentUser } from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  isolation: isolate;
  min-height: calc(var(--100vh) - var(--current-stack-height));
  overflow: hidden;
  position: relative;
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`;

const Content = styled(Vertical).attrs({ center: true, gap: 4 })`
  position: relative;
  z-index: 1;
  flex: 1;
  padding: var(--triple-unit);
  justify-content: center;
  padding-bottom: calc(10 * var(--unit));

  @media ${laptopAndAbove} {
    margin: calc(6 * var(--unit)) 0;
    gap: 104px;
    justify-content: center;
  }
`;

const CTAWrapper = styled.div`
  width: 100%;
  max-width: 480px;
  text-align: center;
`;

const PlayersLeft = styled.img`
  position: absolute;
  left: 0;
  bottom: 0;
  max-width: calc(40 * var(--vw));
`;

const PlayersRight = styled.img`
  position: absolute;
  right: 0;
  bottom: 0;
  max-width: calc(40 * var(--vw));
`;

const PacksImage = styled(Img)`
  max-width: 100%;
  margin-bottom: var(--double-unit);
`;
const PlayersImage = styled(Img)`
  max-width: 100%;
  margin-bottom: -70px;
  margin-top: calc(-10 * var(--unit));
`;

const TextWrapper = styled.div`
  > * {
    filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.55));
  }

  &.multiline {
    width: min-content;
    word-spacing: var(--100vw);
  }
`;

type Props = {
  currentUser?: Nullable<ClaimStarterPack_currentUser>;
  refetch: () => void;
};
export const ClaimStarterPack = ({ currentUser, refetch }: Props) => {
  const getSetConfig = useGetCardSetConfig();
  const findLatestCardSetConfig = useFindLatestCardSetConfig();
  const { generateSportContextPath, sport } = useSportContext();
  const { showTimer, starts, currentSet, nextSet } = useNextSetStartTimer();
  const isLaptopAndAbove = useIsLaptop();
  const seasonOver = !currentSet && !nextSet;

  const setSlug = showTimer ? nextSet?.slug : currentSet?.slug;

  const setConfig = seasonOver
    ? findLatestCardSetConfig(sport)
    : getSetConfig(setSlug);

  const { visual, background, supportedSports } = setConfig;
  const { brand, seasonCountdown } = visual;
  const { logoImage } = brand;
  const supportedSport = supportedSports.find(s => s === sport);
  const showAssets = !!seasonCountdown && supportedSport;
  const showPacksImage = showAssets && !isLaptopAndAbove;
  const showPlayersDesktop = showAssets && isLaptopAndAbove;

  if (!supportedSport) {
    return null;
  }

  return (
    <>
      <Root>
        <Background
          style={
            seasonCountdown
              ? isLaptopAndAbove || !seasonCountdown.backgroundMobile
                ? seasonCountdown.backgroundDesktop[sport]
                : seasonCountdown.backgroundMobile
              : background
          }
        />
        {!!showPlayersDesktop && (
          <>
            <PlayersLeft
              src={seasonCountdown.players[sport]?.desktopLeft}
              alt=""
            />
            <PlayersRight
              src={seasonCountdown.players[sport]?.desktopRight}
              alt=""
            />
          </>
        )}
        <Content gap={1.5} center>
          {!!logoImage && <img src={logoImage} alt="" height={48} />}
          {starts ? (
            <Vertical center gap={2}>
              <TextWrapper className={classNames({ multiline: !seasonOver })}>
                <BrandXXL
                  className="text-gradient-color text-center"
                  style={
                    {
                      '--text-gradient-color': 'var(--c-gradient-common)',
                    } as CSSProperties
                  }
                >
                  {seasonOver ? (
                    <FormattedMessage defaultMessage="The season is over" />
                  ) : (
                    <FormattedMessage defaultMessage="Starts now" />
                  )}
                </BrandXXL>
              </TextWrapper>
              {!!seasonOver && (
                <BrandM className="text-center">
                  <FormattedMessage defaultMessage="Resting up for the comeback" />
                </BrandM>
              )}
            </Vertical>
          ) : (
            <NextSetStartCountdown onEnded={refetch} />
          )}
          <CTAWrapper>
            {!!showPacksImage && (
              <>
                {starts && !seasonOver ? (
                  <PacksImage
                    src={seasonCountdown.packs[sport]!}
                    alt=""
                    width="670"
                    height="464"
                  />
                ) : (
                  <PlayersImage
                    src={seasonCountdown.players[sport]?.mobile}
                    width="750"
                    height="884"
                    alt=""
                  />
                )}
              </>
            )}
            {!seasonOver && (
              <Button
                size="large"
                color="primary"
                blur={!currentSet}
                to={generateSportContextPath(
                  ANY_SPORT_SERIES_ONBOARDING_SELECT_COMPETITION
                )}
                fullWidth
                disabled={!currentSet}
              >
                <FormattedMessage defaultMessage="Claim my starter pack" />
              </Button>
            )}
          </CTAWrapper>
        </Content>
        <AbsoluteNav />
      </Root>
      {!!currentUser && <ClaimWelcomeBackRewards currentUser={currentUser} />}
    </>
  );
};

ClaimStarterPack.fragments = {
  currentUser: gql`
    fragment ClaimStarterPack_currentUser on CurrentUser {
      slug
      ...ClaimWelcomeBackRewards_currentUser
    }
    ${ClaimWelcomeBackRewards.fragments.currentUser}
  ` as TypedDocumentNode<ClaimStarterPack_currentUser>,
};
